import { CommandKind } from '../../engine/commands/CommandBuffer';
import { FIXED_STEP } from '../../engine/core/constants';
import type { GameContext } from '../../engine/core/GameContext';

/**
 * 一段波次。`at`／`duration` 单位秒，`rate` 个/秒；`cluster`／`spread`／`fall` 缺省时用规则级的全局值。
 * 参数形状由 generate-content.mjs 校验，这里只按已校验的形状读取。
 */
export interface WaveParams {
  id: string;
  at: number;
  duration: number;
  rate: number;
  cluster?: number;
  spread?: number;
  fall?: number;
  note?: string;
  /** 大波：HUD 提前 BIG_WAVE_LEAD 秒预告，时间轴高亮，空场催投也只跳到预告起点。 */
  big?: boolean;
  /** 本段的混投表，缺省用规则级的 mix；两者都缺省时用 definition。 */
  mix?: MixEntry[];
}

/** 混投表条目：按权重随机取种，权重相对于同一表内的其他条目。 */
export interface MixEntry {
  definition: string;
  weight: number;
}

export interface SpawnWaveParams {
  /** 入场 y（通常在画面上方）。 */
  entryY: number;
  /** 缺省的史莱姆定义。 */
  definition: string;
  /** 规则级混投表，可被波段覆盖。 */
  mix?: MixEntry[];
  cluster: { size: number; spread: number };
  waves: WaveParams[];
  /** 段间保底涓流（只/秒）：波段之间不再是 0，空屏不会出现。缺省 0。 */
  idleRate?: number;
  /** 空场催投：空中数量低于它且没有波段在投时，直接拨到下一段起点。缺省 0 即关闭。 */
  catchUp?: number;
}

/** 按权重取种。roll 取自随机源的 [0,1)；表为空或权重和为 0 时退回缺省定义。 */
function pickType(ids: ReadonlyMap<string, number>, mix: MixEntry[] | undefined, fallback: string, roll: number): number {
  let chosen = fallback;
  if (mix !== undefined && mix.length > 0) {
    let total = 0;
    for (const entry of mix) total += entry.weight;
    if (total > 0) {
      let cursor = roll * total;
      for (const entry of mix) { cursor -= entry.weight; if (cursor < 0) { chosen = entry.definition; break; } }
      if (cursor >= 0) chosen = mix[mix.length - 1].definition;
    }
  }
  const type = ids.get(chosen);
  if (type === undefined) throw new Error(`Unknown slime definition in spawn wave: ${chosen}`);
  return type;
}

/** 大波预告的提前量（秒）。催投跳到大波时只跳到起点前这么多秒，把预告留出来。 */
export const BIG_WAVE_LEAD = 3;

/** 红线以上的活动史莱姆数，供催投判断。 */
function airborne(context: GameContext): number {
  const { slimes, level } = context;
  let count = 0;
  for (let id = 0; id < slimes.capacity; id += 1) if (slimes.active[id] !== 0 && !slimes.isDying(id) && slimes.y[id] <= level.world.leakLine) count += 1;
  return count;
}

/** 入场时的散布常量。落点结构由内容参数决定，这几项只是让入场不显得整齐划一。 */
const ENTRY_DEPTH = 26;
const ENTRY_DRIFT = 30;
const CLUSTER_MARGIN = 40;
const EDGE_MARGIN = 34;

/**
 * 波次投放（[游戏设计](../../../docs/游戏设计.md#关卡设计)）：关卡是投放时间表，不是坐标表。
 * 波内按速率累积、簇状落点，波间留空档。**投放必须带簇状结构**——落点在整个屏宽均匀随机
 * 会让空中永远是散兵，黏连与环放大都没有素材。
 *
 * 状态放 `context.spawn`（数据结构：有状态规则的契约），随机全部走 `context.random`。
 */
export function runSpawnWave(context: GameContext, params: Record<string, unknown>): void {
  const config = params as unknown as SpawnWaveParams;
  const { state, spawn, level, random, commands, catalog } = context;
  const waves = config.waves;
  const time = state.levelTime;

  let active: WaveParams | undefined;
  let end = 0;
  let index = -1;
  for (let i = 0; i < waves.length; i += 1) {
    const wave = waves[i];
    if (wave.at + wave.duration > end) end = wave.at + wave.duration;
    if (time >= wave.at && time < wave.at + wave.duration) { active = wave; index = i; }
  }
  spawn.waveIndex = index;
  spawn.finished = time >= end;
  if (spawn.finished || state.spawnPaused) return;

  // 段间：空场催投——场快空了就不等，直接拨到下一段起点；否则按保底涓流投。
  // 催投压掉的是段间的等待，与空场提速（压掉收尾的等待）是一对；场空了让敌人快来没有可钻的空子。
  if (active === undefined) {
    let next: WaveParams | undefined;
    for (const wave of waves) if (wave.at > time && (next === undefined || wave.at < next.at)) next = wave;
    const catchUp = config.catchUp ?? 0;
    if (next !== undefined && catchUp > 0 && airborne(context) < catchUp) {
      const target = next.big ? Math.max(time, next.at - BIG_WAVE_LEAD) : next.at;
      if (target > time) { state.levelTime = target; spawn.accumulator = 0; }
      return;
    }
    if ((config.idleRate ?? 0) <= 0) return;
  }

  const rate = active?.rate ?? config.idleRate ?? 0;
  const mix = active?.mix ?? config.mix;
  const size = Math.max(1, Math.round(active?.cluster ?? config.cluster.size));
  const spread = active?.spread ?? config.cluster.spread;
  const fall = active?.fall ?? 1;
  const width = level.world.width;

  spawn.accumulator += rate * FIXED_STEP;
  while (spawn.accumulator >= 1) {
    spawn.accumulator -= 1;
    if (spawn.clusterLeft <= 0) {
      spawn.clusterX = CLUSTER_MARGIN + random.next() * (width - CLUSTER_MARGIN * 2);
      spawn.clusterLeft = size;
    }
    spawn.clusterLeft -= 1;
    const x = Math.max(EDGE_MARGIN, Math.min(width - EDGE_MARGIN, spawn.clusterX + (random.next() - 0.5) * 2 * spread));
    const y = config.entryY - random.next() * ENTRY_DEPTH;
    const vx = (random.next() - 0.5) * ENTRY_DRIFT;
    const type = pickType(catalog.slimeIds, mix, config.definition, random.next());
    // a = 横向初速，b = 落速倍率。竖直初速不传：终端速度只有约 30 px/s，重力几步就接管了。
    commands.lifecycle.push(CommandKind.SpawnSlime, -1, -1, type, x, y, vx, fall);
  }
}
